import { useEffect, useState } from "react";
import type { Deal } from "../types";
import { isCloud } from "../lib/supabase";
import { money0, pct } from "../lib/format";
import {
  listSyndicates, saveSyndicate, deleteSyndicate, newSyndicate, blankMember,
  ownershipPct, capSummary, structureDocHtml, emailStructureDoc,
  MEMBER_ROLES, COMMIT_LABEL, SYNDICATE_STAGES, NON_ACCREDITED_CAP, DISCLAIMER,
  type Syndicate as Syn, type SyndicateMember, type MemberRole, type CommitStatus, type SyndicateStage,
} from "../lib/syndicate";

export function Syndicate({ deals, onOpenDeal }: { deals: Deal[]; onOpenDeal: (id: string) => void }) {
  const [list, setList] = useState<Syn[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [leader, setLeader] = useState("");
  const [gname, setGname] = useState("");

  useEffect(() => { listSyndicates().then(setList).catch(() => {}); }, []);
  const patch = (s: Syn) => { setList((ls) => ls.map((x) => (x.id === s.id ? s : x))); void saveSyndicate(s); };
  const current = list.find((s) => s.id === openId) ?? null;

  function create() {
    if (!leader.trim() || !gname.trim()) return;
    const s = newSyndicate(leader.trim(), gname.trim());
    setList((ls) => [...ls, s]); void saveSyndicate(s);
    setLeader(""); setGname(""); setOpenId(s.id);
  }
  async function remove(id: string) {
    if (!confirm("Delete this syndicate?")) return;
    await deleteSyndicate(id); setList(await listSyndicates()); setOpenId(null);
  }

  if (current) return <Detail s={current} deals={deals} onPatch={patch} onBack={() => setOpenId(null)} onOpenDeal={onOpenDeal} onDelete={() => remove(current.id)} />;

  return (
    <div className="wrap">
      <div className="pe">
        <div className="l">◆ Syndicate — pool capital &amp; expertise, own it together</div>
        <p>
          Do a bigger deal with your <b>own trusted circle</b>. One leader starts a group, everyone contributes
          <b> cash or expertise</b> and earns a real slice, and the leader is compensated for running it. Beacon organizes
          the members, the cap table, the paperwork and a one-page structure doc for your attorney — <b>it never touches
          the money</b> (that stays in escrow) and never gives legal advice. You own the deal; you own the data.
        </p>
      </div>

      <div className="card pad" style={{ marginBottom: 14, borderLeft: "4px solid var(--brass)" }}>
        <div className="eyebrow">Start a syndicate</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 10 }}>
          <input className="bb-name" style={{ minWidth: 180 }} placeholder="Lead sponsor (you)" value={leader} onChange={(e) => setLeader(e.target.value)} />
          <input className="bb-name" style={{ minWidth: 180 }} placeholder="Group name — e.g. Palms Partners" value={gname} onChange={(e) => setGname(e.target.value)} />
          <button className="btn btn-p btn-sm" onClick={create} disabled={!leader.trim() || !gname.trim()}>＋ Create</button>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="note">No syndicates yet. Start one above — you'll be seeded as the accredited lead sponsor of a 506(b) member-managed LLC.</div>
      ) : (
        <div className="grid g2">
          {list.map((s) => {
            const cap = capSummary(s);
            return (
              <button key={s.id} className="card pad tw-card" onClick={() => setOpenId(s.id)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <div style={{ fontWeight: 700, color: "var(--forest)", fontSize: 16 }}>{s.name}</div>
                  <span className="tag t-watch">{s.stage}</span>
                </div>
                <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 2 }}>Led by {s.leaderName} · {cap.members} member{cap.members > 1 ? "s" : ""}</div>
                <div className="bar" style={{ height: 8, marginTop: 12 }}><span style={{ width: Math.min(100, cap.progress * 100) + "%" }} /></div>
                <div style={{ fontSize: 12, color: "var(--forest)", marginTop: 6 }}><b>{money0(cap.committed)}</b> committed of {money0(s.targetRaise)}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Detail({ s, deals, onPatch, onBack, onOpenDeal, onDelete }: {
  s: Syn; deals: Deal[]; onPatch: (s: Syn) => void; onBack: () => void; onOpenDeal: (id: string) => void; onDelete: () => void;
}) {
  const cap = capSummary(s);
  const dealName = deals.find((d) => d.id === s.dealId)?.name;
  const setField = <K extends keyof Syn>(k: K, v: Syn[K]) => onPatch({ ...s, [k]: v });
  const setMember = (id: string, p: Partial<SyndicateMember>) => onPatch({ ...s, members: s.members.map((m) => (m.id === id ? { ...m, ...p } : m)) });
  const addMember = () => onPatch({ ...s, members: [...s.members, blankMember()] });
  const removeMember = (id: string) => onPatch({ ...s, members: s.members.filter((m) => m.id !== id) });
  const toggleStep = (id: string) => onPatch({ ...s, compliance: s.compliance.map((c) => (c.id === id ? { ...c, done: !c.done } : c)) });

  const [to, setTo] = useState(s.leaderEmail || "");
  const [mail, setMail] = useState<{ k: "ok" | "warn"; t: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  async function email() {
    setSending(true); setMail(null);
    try { await emailStructureDoc(s, to.trim(), dealName); setMail({ k: "ok", t: `Structure doc sent to ${to.trim()}.` }); }
    catch (e) { setMail({ k: "warn", t: e instanceof Error ? e.message : String(e) }); }
    finally { setSending(false); }
  }
  function copyDoc() {
    const tmp = document.createElement("div"); tmp.innerHTML = structureDocHtml(s, dealName);
    navigator.clipboard?.writeText(tmp.innerText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); }).catch(() => {});
  }

  const num = (v: string) => Number(v.replace(/[^0-9.]/g, "")) || 0;

  return (
    <div className="wrap">
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <button className="btn btn-g btn-sm" onClick={onBack}>← Syndicates</button>
        <h2 className="sechead" style={{ margin: 0, fontSize: 22 }}>{s.name}</h2>
        <select className="bb-name" style={{ minWidth: 120 }} value={s.stage} onChange={(e) => setField("stage", e.target.value as SyndicateStage)}>
          {SYNDICATE_STAGES.map((st) => <option key={st} value={st}>{st}</option>)}
        </select>
        <div className="sp" style={{ flex: 1 }} />
        <button className="rp-x" title="Delete" onClick={onDelete}>✕</button>
      </div>

      <div className="flag warn" style={{ marginBottom: 12 }}>
        <span>◐</span><div><b>Software, not advice.</b> Beacon never holds funds (they stay in your escrow/bank) and this is a working draft for your securities attorney — not a securities offering or legal advice.</div>
      </div>

      {/* OVERVIEW */}
      <div className="grid g4" style={{ marginBottom: 6 }}>
        <div className="card kpi"><div className="kl">Target raise</div><div className="kv">{money0(s.targetRaise)}</div></div>
        <div className="card kpi"><div className="kl">Committed cash</div><div className="kv">{money0(cap.committed)}</div><div className="ks">{pct(cap.progress, 0)} of target</div></div>
        <div className="card kpi"><div className="kl">Sweat equity</div><div className="kv">{money0(cap.sweatTotal)}</div><div className="ks">valued expertise</div></div>
        <div className="card kpi"><div className="kl">Members</div><div className="kv">{cap.members}</div><div className={"ks" + (cap.capExceeded ? " bad" : "")}>{cap.nonAccredited} non-accredited</div></div>
      </div>
      <div className="bar" style={{ height: 10, margin: "6px 0 12px" }}><span style={{ width: Math.min(100, cap.progress * 100) + "%" }} /></div>
      {cap.capExceeded && <div className="flag bad" style={{ marginBottom: 12 }}><span>!</span><div>{cap.nonAccredited} non-accredited members exceeds the Reg D 506(b) cap of {NON_ACCREDITED_CAP}. Restructure (accredited-only, or fewer non-accredited) with counsel.</div></div>}

      {/* LINK DEAL + TARGET */}
      <div className="card pad" style={{ marginBottom: 12 }}>
        <div className="bb-grid">
          <label className="bb-f"><span>Target raise (cash $)</span><input type="number" value={s.targetRaise || ""} onChange={(e) => setField("targetRaise", num(e.target.value))} /></label>
          <label className="bb-f"><span>Entity</span><input value={s.entity} onChange={(e) => setField("entity", e.target.value)} /></label>
          <label className="bb-f"><span>Exemption</span><input value={s.exemption} onChange={(e) => setField("exemption", e.target.value)} /></label>
          <label className="bb-f"><span>Target property</span>
            <select value={s.dealId || ""} onChange={(e) => setField("dealId", e.target.value || undefined)}>
              <option value="">— none linked —</option>
              {deals.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select></label>
        </div>
        {s.dealId && <div style={{ marginTop: 8 }}><button className="btn btn-g btn-sm" onClick={() => onOpenDeal(s.dealId!)}>Open {dealName} →</button></div>}
      </div>

      {/* MEMBERS + CAP TABLE */}
      <h2 className="sechead" style={{ fontSize: 18 }}>Members &amp; cap table — cash + sweat equity</h2>
      <div className="card pad" style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <div className="eyebrow">Everyone owns a slice — capital or expertise</div>
          <button className="btn btn-a btn-sm" onClick={addMember}>+ Add member</button>
        </div>
        <div className="tablewrap" style={{ marginTop: 10 }}>
          <table className="t">
            <thead><tr><th>Name</th><th>Role</th><th className="n">Cash</th><th className="n">Sweat</th><th>Brings</th><th>Accred.</th><th>Status</th><th className="n">Own %</th><th></th></tr></thead>
            <tbody>
              {s.members.map((m) => (
                <tr key={m.id}>
                  <td><input className="rp-in" style={{ minWidth: 110 }} value={m.name} placeholder="name" onChange={(e) => setMember(m.id, { name: e.target.value })} /></td>
                  <td><select className="rp-in" value={m.role} onChange={(e) => setMember(m.id, { role: e.target.value as MemberRole })}>{MEMBER_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}</select></td>
                  <td className="n"><input className="rp-in n" type="number" min={0} style={{ width: 88, textAlign: "right" }} value={m.cash || ""} placeholder="0" onChange={(e) => setMember(m.id, { cash: num(e.target.value) })} /></td>
                  <td className="n"><input className="rp-in n" type="number" min={0} style={{ width: 84, textAlign: "right" }} value={m.sweat || ""} placeholder="0" onChange={(e) => setMember(m.id, { sweat: num(e.target.value) })} /></td>
                  <td><input className="rp-in" style={{ minWidth: 100 }} value={m.sweatNote || ""} placeholder="GC, legal, PM…" onChange={(e) => setMember(m.id, { sweatNote: e.target.value })} /></td>
                  <td><input type="checkbox" checked={m.accredited} onChange={(e) => setMember(m.id, { accredited: e.target.checked })} /></td>
                  <td><select className="rp-in" value={m.status} onChange={(e) => setMember(m.id, { status: e.target.value as CommitStatus })}>{Object.entries(COMMIT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></td>
                  <td className="n" style={{ fontWeight: 700, color: "var(--forest)" }}>{pct(ownershipPct(m, s.members))}</td>
                  <td>{m.role !== "Sponsor" && <button className="rp-x" title="Remove" onClick={() => removeMember(m.id)}>✕</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="note" style={{ marginTop: 8 }}>Ownership % = each member's <b>cash + sweat</b> ÷ the whole pool. Sweat-equity grants are taxed differently than cash — confirm the profits-interest / 83(b) treatment with your CPA.</div>
      </div>

      {/* SPONSOR ECONOMICS */}
      <h2 className="sechead" style={{ fontSize: 18 }}>Sponsor economics — the leader gets paid to run it</h2>
      <div className="card pad" style={{ marginBottom: 12 }}>
        <div className="bb-grid">
          <label className="bb-f"><span>Acquisition fee %</span><input type="number" step="0.5" value={s.acqFeePct * 100 || ""} onChange={(e) => setField("acqFeePct", num(e.target.value) / 100)} /></label>
          <label className="bb-f"><span>Asset-mgmt fee %/yr</span><input type="number" step="0.5" value={s.amFeePct * 100 || ""} onChange={(e) => setField("amFeePct", num(e.target.value) / 100)} /></label>
          <label className="bb-f"><span>Preferred return %</span><input type="number" step="0.5" value={s.prefPct * 100 || ""} onChange={(e) => setField("prefPct", num(e.target.value) / 100)} /></label>
          <label className="bb-f"><span>Promote (carry) %</span><input type="number" step="1" value={s.promotePct * 100 || ""} onChange={(e) => setField("promotePct", num(e.target.value) / 100)} /></label>
        </div>
        <div className="flag ok" style={{ marginTop: 10 }}>
          <span>◆</span><div>The waterfall: members get their capital back, then a <b>{pct(s.prefPct, 0)} preferred return</b>, then profit above that splits <b>{Math.round((1 - s.promotePct) * 100)}/{Math.round(s.promotePct * 100)}</b> — the {pct(s.promotePct, 0)} is the leader's promote for sourcing &amp; running the deal.</div>
        </div>
      </div>

      {/* COMPLIANCE */}
      <h2 className="sechead" style={{ fontSize: 18 }}>Formation &amp; compliance — your attorney's checklist</h2>
      <div className="card pad" style={{ marginBottom: 12 }}>
        {s.compliance.map((c, i) => (
          <div key={c.id} style={{ display: "flex", gap: 11, alignItems: "center", padding: "9px 0", borderBottom: i === s.compliance.length - 1 ? "none" : "1px solid var(--line)" }}>
            <input type="checkbox" checked={c.done} onChange={() => toggleStep(c.id)} style={{ width: 17, height: 17 }} />
            <span style={{ fontSize: 13.5, color: c.done ? "var(--muted)" : "var(--forest)", textDecoration: c.done ? "line-through" : "none" }}>{c.label}</span>
          </div>
        ))}
        <div className="note" style={{ marginTop: 8 }}>Beacon guides the process; your securities attorney owns it. These aren't legal advice — they're the standard steps to review together.</div>
      </div>

      {/* THE ONE-PAGE DOC */}
      <h2 className="sechead" style={{ fontSize: 18 }}>One-page structure — email it to your attorney</h2>
      <div className="card pad">
        <div className="doc-frame" dangerouslySetInnerHTML={{ __html: structureDocHtml(s, dealName) }} />
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 12, borderTop: "1px dashed var(--line)", paddingTop: 12 }}>
          <input type="email" className="bb-name" style={{ minWidth: 220 }} placeholder="attorney@lawfirm.com" value={to} onChange={(e) => setTo(e.target.value)} />
          <button className="btn btn-p btn-sm" onClick={email} disabled={!isCloud() || sending || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(to.trim())}>{sending ? "Sending…" : "✉ Email to attorney (as APAS Build)"}</button>
          <button className="btn btn-g btn-sm" onClick={copyDoc}>{copied ? "✓ Copied" : "⧉ Copy"}</button>
          <button className="btn btn-g btn-sm" onClick={() => window.print()}>🖨 Print / PDF</button>
        </div>
        {!isCloud() && <div className="note" style={{ marginTop: 6 }}>Emailing runs in cloud mode (Resend). Print or copy works anywhere.</div>}
        {mail && <div className={"flag " + (mail.k === "ok" ? "ok" : "warn")} style={{ marginTop: 8 }}><span>{mail.k === "ok" ? "✓" : "◐"}</span><div>{mail.t}</div></div>}
        <div className="note" style={{ marginTop: 10, fontSize: 10.5 }}>{DISCLAIMER}</div>
      </div>
    </div>
  );
}
